import {useEffect, useSyncExternalStore} from 'react'

export type AhpToolTiming = {
  /** ISO time this client first saw the call executing. */
  startedAt: string
  /** Execution time so far, set once the call is paused, waiting or finished. */
  durationMs?: number
  /** ISO time to count elapsed execution from while the call is executing. */
  elapsedFrom?: string
}

/** `waiting` covers argument streaming, confirmation and authentication, which do not count as execution. */
export type AhpToolPhase = 'executing' | 'waiting' | 'done'

type Entry = {
  startedAtMs: number
  accumulatedMs: number
  executingSinceMs?: number
  finished: boolean
  timing: AhpToolTiming
}

const MAX_ENTRIES = 1000
const entries = new Map<string, Entry>()
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

function evictIfFull() {
  if (entries.size <= MAX_ENTRIES) return
  for (const [key, entry] of entries) {
    if (entry.finished) {
      entries.delete(key)
      return
    }
  }
  const oldest = entries.keys().next().value
  if (oldest !== undefined) entries.delete(oldest)
}

function pause(entry: Entry, nowMs: number) {
  if (entry.executingSinceMs !== undefined) {
    entry.accumulatedMs += Math.max(0, nowMs - entry.executingSinceMs)
    entry.executingSinceMs = undefined
  }
  entry.timing = {startedAt: entry.timing.startedAt, durationMs: entry.accumulatedMs}
}

/**
 * AHP carries no per-tool timestamps, so timing comes from the phase changes this client
 * observes. Calls first seen waiting or done (history, reconnect) have no known start.
 */
export function recordAhpToolTiming(key: string, phase: AhpToolPhase, nowMs: number): boolean {
  const entry = entries.get(key)
  if (!entry) {
    if (phase !== 'executing') return false
    const startedAt = new Date(nowMs).toISOString()
    entries.set(key, {
      startedAtMs: nowMs,
      accumulatedMs: 0,
      executingSinceMs: nowMs,
      finished: false,
      timing: {startedAt, elapsedFrom: startedAt},
    })
    evictIfFull()
    return true
  }
  if (entry.finished) return false

  if (phase === 'executing') {
    if (entry.executingSinceMs !== undefined) return false
    entry.executingSinceMs = nowMs
    entry.timing = {startedAt: entry.timing.startedAt, elapsedFrom: new Date(nowMs - entry.accumulatedMs).toISOString()}
    return true
  }
  if (phase === 'waiting') {
    if (entry.executingSinceMs === undefined) return false
    pause(entry, nowMs)
    return true
  }
  pause(entry, nowMs)
  entry.finished = true
  return true
}

export function ahpToolTimingKey(taskId: string | undefined, chatResource: string | undefined, toolCallId: string) {
  return `${taskId ?? ''}\u0000${chatResource ?? ''}\u0000${toolCallId}`
}

export function finishAhpToolTimings(keys: readonly string[], nowMs: number) {
  let changed = false
  for (const key of keys) {
    if (recordAhpToolTiming(key, 'done', nowMs)) changed = true
  }
  if (changed) notify()
}

/** Drops unfinished timing, since phase changes are not observed while no row is mounted. */
export function forgetUnfinishedAhpToolTiming(key: string): boolean {
  const entry = entries.get(key)
  if (!entry || entry.finished) return false
  entries.delete(key)
  return true
}

export function resetAhpToolTimingForTests() {
  entries.clear()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getServerSnapshot = () => undefined

export function useAhpToolTiming(key: string, phase: AhpToolPhase, enabled = true): AhpToolTiming | undefined {
  useEffect(() => {
    if (enabled && recordAhpToolTiming(key, phase, Date.now())) notify()
  }, [key, phase, enabled])

  useEffect(() => {
    if (!enabled) return
    return () => {
      if (forgetUnfinishedAhpToolTiming(key)) notify()
    }
  }, [key, enabled])

  return useSyncExternalStore(subscribe, () => (enabled ? entries.get(key)?.timing : undefined), getServerSnapshot)
}
